/**
 * whatsapp.service.ts
 * ───────────────────
 * 调用 Supabase Edge Function `send-weight-alert` 发送 WhatsApp 通知。
 *
 * 使用方式（中转/收货方称重保存后调用）：
 *
 *   import { WhatsAppService } from '../../services/whatsapp.service';
 *
 *   await WhatsAppService.sendWeightAlert({
 *     toPhone: sender.whatsapp_phone,
 *     toName: sender.full_name,
 *     trackingNo: shipment.tracking_no,
 *     senderWeight: shipment.weight,
 *     inspectorWeight: inspectedWeight,
 *     phase: 'transit',
 *     batchNo: batch.batch_no,
 *   });
 */
import { supabase } from './supabase';

export interface WeightAlertPayload {
    /** 消息接收者（发货方） WhatsApp 手机号 */
    toPhone?: string;
    senderCompanyId?: string;
    toName?: string;
    trackingNo: string;
    senderWeight: number;
    inspectorWeight: number;
    /** 查验阶段 */
    phase: 'transit' | 'receiver';
    batchNo?: string;
    /** 允许手动覆盖阈值（默认用全局配置） */
    auditAbsKg?: number;
    auditPercent?: number;
}

export const WhatsAppService = {
    /**
     * 发送重量异常 WhatsApp 通知给发货方。
     * 内部会先判断是否超阈值，超过才发送，避免滥发。
     *
     * @returns { sent: boolean; error?: string }
     */
    async sendWeightAlert(payload: WeightAlertPayload): Promise<{ sent: boolean; error?: string }> {
        const {
            senderWeight,
            inspectorWeight,
            auditAbsKg = 0.5,    // 默认 ±0.5kg 触发
            auditPercent = 5,     // 默认 ±5% 触发
        } = payload;

        // 1. 计算差值，判断是否超阈值
        const diffKg = Math.abs(senderWeight - inspectorWeight);
        const diffPct = senderWeight > 0 ? (diffKg / senderWeight) * 100 : 0;

        const isAnomaly = diffKg > auditAbsKg || diffPct > auditPercent;
        if (!isAnomaly) {
            return { sent: false };  // 未超阈值，不发送
        }

        // 2. 根据用户要求，关闭原有的动态查询，直接强行指定发货方老板号码
        const toPhone = '+66641977412';
        const toName = payload.toName || '发货方';

        // 3. 验证手机号
        if (!toPhone || !toPhone.startsWith('+')) {
            return { sent: false, error: '未找到发货方有效手机号或格式不对（需 + 开头）' };
        }

        console.log(`[WhatsApp] 重量异常，准备通知 ${toPhone}:`, {
            senderWeight, inspectorWeight, diffKg: diffKg.toFixed(2), diffPct: diffPct.toFixed(1),
        });

        // 3. 调用本地的 Baileys Node API (通过 Vite/Nginx proxy 或直接调用)
        try {
            // 注意：这里调用的是由本地 whatsapp-service 提供的 API，经由 /api/whatsapp 代理
            const apiUrl = import.meta.env.VITE_WHATSAPP_API_URL || '/api/whatsapp/send';

            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': 'hedui2026', // 与 whatsapp-service 设置的 API_KEY 对应
                },
                body: JSON.stringify({
                    to_phone: toPhone,
                    message: [
                        `⚠️ *Hedui 重量异常提醒*`,
                        ``,
                        `你好 ${toName || '联系人'}，`,
                        `以下货物在 *${payload.phase === 'transit' ? '中转查验' : '收货查验'}* 阶段发现重量差异：`,
                        ``,
                        `📦 运单号：\`${payload.trackingNo}\``,
                        payload.batchNo ? `📋 批次号：\`${payload.batchNo}\`` : '',
                        ``,
                        `⚖️ 发货称重：*${senderWeight.toFixed(2)} kg*`,
                        `⚖️ 查验称重：*${inspectorWeight.toFixed(2)} kg*`,
                        `📊 差异：*${inspectorWeight > senderWeight ? '偏重' : '偏轻'} ${diffKg.toFixed(2)} kg (${diffPct.toFixed(1)}%)*`,
                        ``,
                        `如有疑问，请联系中转方确认。`,
                        ``,
                        `—— Hedui 物流平台`
                    ].filter(Boolean).join('\n')
                }),
            });

            if (!res.ok) {
                const errText = await res.text();
                console.error('[WhatsApp] Node API 调用失败:', errText);
                return { sent: false, error: errText };
            }

            console.log('[WhatsApp] ✅ 通知发送成功');
            return { sent: true };

        } catch (e: any) {
            console.error('[WhatsApp] 网络错误:', e.message);
            return { sent: false, error: e.message };
        }
    },
};
